import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, entrarDelegado } from '../lib/api';
import { Card } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DialogoConfirmar } from '../components/ui/confirmar';
import { toast } from 'sonner';
import { fmtFull, humanSize } from '../lib/format';
import { useStore } from '../store/useStore';
import PanelGrupos from '../components/admin/PanelGrupos';
import PanelSso from '../components/admin/PanelSso';
import PanelMarcaApp from '../components/admin/PanelMarcaApp';
import { Users, Mail, Database, Shield, Trash2, Ban, Unlock, Globe, Pencil, Plus, RefreshCw, PlugZap, FolderSync, Image as ImageIcon, Upload, LogIn, LogOut } from 'lucide-react';

function Stat({ icon: Icon, label, value }) {
  return (
    <Card className="p-5 flex items-center gap-4">
      <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center"><Icon className="h-6 w-6" /></div>
      <div><div className="text-2xl font-bold font-display">{value}</div><div className="text-sm text-muted-foreground">{label}</div></div>
    </Card>
  );
}

const EMPTY_DOMAIN = { domain: '', imap_host: 'imap.hostinger.com', imap_port: 993, smtp_host: 'smtp.hostinger.com', smtp_port: 465, enabled: true, notes: '' };

const FOLDER_LABELS = { inbox: 'Recibidos', sent: 'Enviados', archive: 'Archivo', spam: 'Spam', trash: 'Papelera' };

const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
const LOGO_MAX_BYTES = 2 * 1024 * 1024;

/** Acepta un dominio, un correo completo o una URL pegada y deja solo el dominio. */
function normalizeDomain(value) {
  let v = (value || '').trim().toLowerCase();
  v = v.replace(/^[a-z]+:\/\//, '');          // https://…
  if (v.includes('@')) v = v.split('@').pop(); // persona@empresa.com
  v = v.split('/')[0];                         // …/ruta
  return v.replace(/^\.+|\.+$/g, '');
}

/** Vista previa del logo: la imagen va autenticada, así que se pide como blob
 *  (una etiqueta <img src> no manda el token) y el objectURL se libera al salir. */
function LogoPreview({ domain, ok, version }) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    setUrl(null);
    if (!ok) return undefined;
    let alive = true;
    let objectUrl = null;
    api.get('/brand/logo', { params: { domain }, responseType: 'blob' })
      .then((r) => {
        if (!alive || !r.data || !r.data.size) return;
        objectUrl = URL.createObjectURL(r.data);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [domain, ok, version]);

  const box = {
    width: 36, height: 36, borderRadius: 9, flex: 'none',
    background: 'var(--panel-2)', border: '1px solid var(--line)',
  };

  if (!url) {
    return (
      <div
        className="grid place-items-center font-semibold select-none"
        style={{ ...box, background: 'var(--hover)', color: 'var(--text-3)', fontSize: 12 }}
        title={ok ? 'Cargando…' : 'Sin logo'}
      >
        {(domain || '?').slice(0, 2).toUpperCase()}
      </div>
    );
  }
  return (
    <div className="grid place-items-center overflow-hidden" style={box}>
      <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 3 }} />
    </div>
  );
}

export default function Admin() {
  const qc = useQueryClient();
  const { user: me, setUser } = useStore();
  const { data: stats } = useQuery({ queryKey: ['admin-stats'], queryFn: async () => (await api.get('/admin/stats')).data });
  const { data: users = [] } = useQuery({ queryKey: ['admin-users'], queryFn: async () => (await api.get('/admin/users')).data });
  const { data: audit = [] } = useQuery({ queryKey: ['admin-audit'], queryFn: async () => (await api.get('/admin/audit')).data });
  const { data: sessions = [] } = useQuery({ queryKey: ['admin-sessions'], queryFn: async () => (await api.get('/admin/sessions')).data });
  const { data: blocks = [] } = useQuery({ queryKey: ['admin-blocks'], queryFn: async () => (await api.get('/admin/blocks')).data, refetchInterval: 30000 });
  const { data: domains = [] } = useQuery({ queryKey: ['admin-domains'], queryFn: async () => (await api.get('/admin/domains')).data });
  const { data: sync = [], refetch: refetchSync, isFetching: syncFetching } = useQuery({ queryKey: ['admin-sync'], queryFn: async () => (await api.get('/admin/sync')).data, refetchInterval: 30000 });
  const { data: brandLogos = [] } = useQuery({ queryKey: ['admin-brand-logos'], queryFn: async () => (await api.get('/admin/brand/logos')).data });

  const [confirmar, setConfirmar] = useState(null);         // { titulo, mensaje, boton, accion } (M27)
  // --- Dominios ---
  const [domDialog, setDomDialog] = useState(null);       // null | {mode:'create'|'edit', form:{...}, id?}
  const [testDialog, setTestDialog] = useState(null);     // null | {domain, email, password, busy}

  const openCreate = () => setDomDialog({ mode: 'create', form: { ...EMPTY_DOMAIN } });
  const openEdit = (d) => setDomDialog({ mode: 'edit', id: d.id, form: { domain: d.domain, imap_host: d.imap_host, imap_port: d.imap_port, smtp_host: d.smtp_host, smtp_port: d.smtp_port, enabled: d.enabled, notes: d.notes || '' } });
  const setForm = (patch) => setDomDialog((s) => ({ ...s, form: { ...s.form, ...patch } }));

  const saveDomain = async () => {
    const { mode, id, form } = domDialog;
    if (!form.domain.trim()) { toast.error('Escribe el dominio'); return; }
    const payload = { ...form, imap_port: Number(form.imap_port) || 993, smtp_port: Number(form.smtp_port) || 465 };
    try {
      if (mode === 'create') await api.post('/admin/domains', payload);
      else await api.put(`/admin/domains/${id}`, payload);
      qc.invalidateQueries({ queryKey: ['admin-domains'] });
      setDomDialog(null);
      toast.success(mode === 'create' ? 'Dominio agregado' : 'Dominio actualizado');
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo guardar el dominio'); }
  };

  const toggleDomain = async (d) => {
    try {
      await api.put(`/admin/domains/${d.id}`, { domain: d.domain, imap_host: d.imap_host, imap_port: d.imap_port, smtp_host: d.smtp_host, smtp_port: d.smtp_port, enabled: !d.enabled, notes: d.notes || '' });
      qc.invalidateQueries({ queryKey: ['admin-domains'] });
      toast.success(!d.enabled ? `${d.domain} activado` : `${d.domain} desactivado: nadie de ese dominio podrá entrar`);
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo actualizar'); }
  };

  // Diálogos propios en vez de window.confirm (M27): el mismo criterio y aspecto en todo el producto.
  const deleteDomain = (d) => setConfirmar({
    titulo: `¿Eliminar ${d.domain}?`,
    mensaje: 'Sus usuarios ya no podrán entrar a la plataforma.',
    boton: 'Eliminar dominio',
    accion: () => borrarDominio(d),
  });
  const borrarDominio = async (d) => {
    try {
      await api.delete(`/admin/domains/${d.id}`);
      qc.invalidateQueries({ queryKey: ['admin-domains'] });
      toast.success('Dominio eliminado');
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo eliminar'); }
  };

  const runTest = async () => {
    const { email, password } = testDialog;
    if (!email || !password) { toast.error('Escribe correo y contraseña de prueba'); return; }
    setTestDialog((s) => ({ ...s, busy: true }));
    try {
      const res = (await api.post('/admin/domains/test', { email, password })).data;
      if (res.ok) { toast.success('Credenciales válidas — conexión IMAP correcta'); setTestDialog(null); }
      else if (res.auth_failed) toast.error('El servidor rechazó las credenciales');
      else toast.error(`No se pudo contactar el servidor: ${res.error || 'sin detalle'}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al probar la conexión'); }
    setTestDialog((s) => (s ? { ...s, busy: false } : s));
  };

  // --- Logos de remitentes ---
  // Los que fallaron primero (son los que piden atención), luego los manuales.
  const sortedLogos = useMemo(() => {
    const rank = (l) => (!l.ok ? 0 : l.manual ? 1 : 2);
    return [...brandLogos].sort((a, b) => rank(a) - rank(b) || a.domain.localeCompare(b.domain));
  }, [brandLogos]);

  const [logoDialog, setLogoDialog] = useState(null);   // null | {domain, file, previewUrl, busy}
  const [logoVersions, setLogoVersions] = useState({}); // fuerza recargar la vista previa tras subir

  const openLogoDialog = (domain = '') => setLogoDialog({ domain, file: null, previewUrl: null, busy: false });

  const closeLogoDialog = () => {
    setLogoDialog((s) => {
      if (s?.previewUrl) URL.revokeObjectURL(s.previewUrl);
      return null;
    });
  };

  const pickLogoFile = (file) => {
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) { toast.error('Usa una imagen PNG, JPG o WebP'); return; }
    if (file.size > LOGO_MAX_BYTES) { toast.error('La imagen no debe pasar de 2 MB'); return; }
    setLogoDialog((s) => {
      if (s?.previewUrl) URL.revokeObjectURL(s.previewUrl);
      return { ...s, file, previewUrl: URL.createObjectURL(file) };
    });
  };

  const saveLogo = async () => {
    const domain = normalizeDomain(logoDialog?.domain);
    if (!domain) { toast.error('Escribe el dominio'); return; }
    if (!logoDialog?.file) { toast.error('Elige una imagen'); return; }
    setLogoDialog((s) => ({ ...s, busy: true }));
    try {
      const fd = new FormData();
      fd.append('domain', domain);
      fd.append('file', logoDialog.file);
      await api.post('/admin/brand/logo', fd);
      setLogoVersions((v) => ({ ...v, [domain]: (v[domain] || 0) + 1 }));
      qc.invalidateQueries({ queryKey: ['admin-brand-logos'] });
      closeLogoDialog();
      toast.success(`Logo guardado para ${domain}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar el logo');
      setLogoDialog((s) => (s ? { ...s, busy: false } : s));
    }
  };

  const deleteLogo = (l) => setConfirmar({
    titulo: `¿Quitar el logo de ${l.domain}?`,
    mensaje: 'Se volverá a buscar automáticamente a partir de su sitio web.',
    boton: 'Quitar logo',
    accion: () => quitarLogo(l),
  });
  const quitarLogo = async (l) => {
    try {
      await api.delete('/admin/brand/logo', { params: { domain: l.domain } });
      setLogoVersions((v) => ({ ...v, [l.domain]: (v[l.domain] || 0) + 1 }));
      qc.invalidateQueries({ queryKey: ['admin-brand-logos'] });
      toast.success('Logo eliminado');
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo eliminar'); }
  };

  // --- Sincronización ---
  const reindex = (row) => setConfirmar({
    titulo: `¿Reindexar ${FOLDER_LABELS[row.folder] || row.folder} de ${row.email}?`,
    mensaje: 'Se borra la caché de esa carpeta y se reconstruye sola.',
    boton: 'Reindexar',
    tono: 'normal',
    accion: () => reindexar(row),
  });
  const reindexar = async (row) => {
    try {
      await api.post('/admin/sync/reindex', { account_id: row.account_id, folder: row.folder });
      qc.invalidateQueries({ queryKey: ['admin-sync'] });
      toast.success('Reindexación iniciada — la carpeta se reconstruye en segundo plano');
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo reindexar'); }
  };

  /* --- Entrar al buzón de otra persona ---
     Se recarga la página a propósito: cambiar de sesión en caliente dejaría en
     la caché de react-query (y en el WebSocket) el correo del admin mezclado
     con el del buzón visitado. Un arranque limpio es la única forma honesta. */
  const [entrando, setEntrando] = useState(null);   // id del buzón que se está abriendo

  const entrarBuzon = async (destino) => {
    if (!destino || entrando) return;
    setEntrando(destino.id);
    try {
      const res = (await api.post(`/admin/users/${destino.id}/impersonate`)).data;
      entrarDelegado({ access: res.access_token, user: res.user, quien: res.impersonated_by });
      setUser(res.user);
      window.location.href = '/mail';
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo entrar al buzón');
      setEntrando(null);
    }
  };

  /* La delegación caduca sola (o la revocan). El interceptor devuelve al admin
     aquí con una marca; el aviso lo damos una vez y la borramos. */
  useEffect(() => {
    try {
      if (sessionStorage.getItem('zuno_deleg_fin')) {
        sessionStorage.removeItem('zuno_deleg_fin');
        toast.info('El acceso al buzón caducó. Vuelves a tu sesión.');
      }
    } catch (_) {}
  }, []);

  // Un clic y sin aviso si fallaba (A12). Desbloquear vuelve a dar acceso: se confirma.
  // Con sesiones sin vencimiento, esta es la forma de sacar a alguien (S2, auditoría 2026-10-05).
  const cerrarSesiones = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/cerrar-sesiones`);
      toast.success(data.cerradas ? `Se cerraron ${data.cerradas} sesiones de ${u.email}` : `${u.email} no tenía sesiones abiertas`);
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudieron cerrar las sesiones'); }
  };

  const unblock = (b) => setConfirmar({
    titulo: '¿Quitar este bloqueo?',
    mensaje: `${b.type === 'ip' ? 'La IP' : 'La cuenta'} ${b.value || ''} podrá volver a intentar entrar. Hazlo solo si sabes que los intentos fallidos fueron de la persona y no de un ataque.`,
    boton: 'Desbloquear',
    tono: 'normal',
    accion: () => desbloquear(b.id),
  });
  const desbloquear = async (id) => {
    try {
      await api.delete(`/admin/blocks/${id}`);
      toast.success('Desbloqueado');
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo desbloquear'); }
    qc.invalidateQueries({ queryKey: ['admin-blocks'] });
  };

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-6xl mx-auto p-6">
        <h1 className="font-display text-2xl font-bold mb-1 flex items-center gap-2"><Shield className="h-6 w-6 text-primary" />Panel de administración</h1>
        <p className="text-muted-foreground mb-6">Gestión de tu organización Orvian.</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Stat icon={Users} label="Usuarios" value={stats?.users ?? '-'} />
          <Stat icon={Mail} label="Cuentas" value={stats?.accounts ?? '-'} />
          <Stat icon={Database} label="Almacenamiento" value={stats ? humanSize(stats.storage_bytes) : '-'} />
          <Stat icon={Ban} label="Bloqueos" value={blocks.length} />
        </div>
        <Tabs defaultValue="domains">
          <TabsList className="mb-4 flex-wrap h-auto">
            <TabsTrigger value="domains" data-testid="admin-tab-domains">Dominios</TabsTrigger>
            <TabsTrigger value="logos" data-testid="admin-tab-logos">Logos</TabsTrigger>
            <TabsTrigger value="marca" data-testid="admin-tab-marca">Marca de la app</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Usuarios</TabsTrigger>
            <TabsTrigger value="grupos" data-testid="admin-tab-grupos">Grupos</TabsTrigger>
            <TabsTrigger value="sso" data-testid="admin-tab-sso">Accesos SSO</TabsTrigger>
            <TabsTrigger value="sync" data-testid="admin-tab-sync">Sincronización</TabsTrigger>
            <TabsTrigger value="security" data-testid="admin-tab-security">Seguridad</TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit">Auditoría</TabsTrigger>
            <TabsTrigger value="sessions" data-testid="admin-tab-sessions">Sesiones</TabsTrigger>
          </TabsList>

          <TabsContent value="domains" className="space-y-4">
            <Card className="p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-medium flex items-center gap-2"><Globe className="h-4 w-4 text-primary" />Dominios permitidos</div>
                  <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    Los buzones se crean en el proveedor (Hostinger). Aquí defines qué dominios pueden entrar y con qué
                    servidores IMAP/SMTP. Los empleados entran con el correo y contraseña de su buzón.
                  </p>
                </div>
                <Button className="gap-1.5" onClick={openCreate} data-testid="domain-add"><Plus className="h-4 w-4" />Agregar dominio</Button>
              </div>
            </Card>
            <Card data-testid="admin-domains-table">
              <Table>
                <TableHeader><TableRow><TableHead>Dominio</TableHead><TableHead>IMAP</TableHead><TableHead>SMTP</TableHead><TableHead>Estado</TableHead><TableHead>Notas</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>{domains.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Sin dominios — nadie puede entrar</TableCell></TableRow> : domains.map((d) => (
                  <TableRow key={d.id} data-testid="domain-row">
                    <TableCell className="font-medium">{d.domain}</TableCell>
                    <TableCell className="text-sm text-muted-foreground font-mono">{d.imap_host}:{d.imap_port}</TableCell>
                    <TableCell className="text-sm text-muted-foreground font-mono">{d.smtp_host}:{d.smtp_port}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={d.enabled}
                          aria-label={`Permitir el acceso a ${d.domain}`}
                          onCheckedChange={() => (d.enabled
                            // Desactivar deja fuera a todo un dominio: se confirma (S7). Activar no.
                            ? setConfirmar({
                              titulo: `¿Desactivar ${d.domain}?`,
                              mensaje: 'Nadie de ese dominio podrá entrar, y las sesiones abiertas se cerrarán en su próxima renovación (como mucho en una hora).',
                              boton: 'Desactivar dominio',
                              accion: () => toggleDomain(d),
                            })
                            : toggleDomain(d))}
                          data-testid="domain-toggle"
                        />
                        {d.enabled ? <Badge>Activo</Badge> : <Badge variant="outline">Inactivo</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground truncate max-w-[160px]">{d.notes}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Probar conexión" aria-label={`Probar la conexión de ${d.domain}`} onClick={() => setTestDialog({ domain: d.domain, email: `@${d.domain}`, password: '', busy: false })} data-testid="domain-test"><PlugZap className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Editar" aria-label={`Editar ${d.domain}`} onClick={() => openEdit(d)} data-testid="domain-edit"><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Eliminar" aria-label={`Eliminar ${d.domain}`} onClick={() => deleteDomain(d)} data-testid="domain-delete"><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>))}</TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="logos" className="space-y-4">
            <Card className="p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-medium flex items-center gap-2"><ImageIcon className="h-4 w-4 text-primary" />Logos de remitentes</div>
                  <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    El logo de cada empresa se busca solo a partir del dominio del remitente. Algunos sitios bloquean
                    esa búsqueda o no tienen web (hay dominios que solo sirven para enviar correo): ahí puedes poner el
                    logo a mano y ese se respeta siempre. Los correos de tu propia organización nunca llevan logo —
                    muestran la foto o las iniciales de la persona.
                  </p>
                </div>
                <Button className="gap-1.5" onClick={() => openLogoDialog('')} data-testid="logo-add"><Plus className="h-4 w-4" />Añadir logo</Button>
              </div>
            </Card>
            <Card data-testid="admin-logos-table">
              <Table>
                <TableHeader><TableRow><TableHead className="w-[64px]"></TableHead><TableHead>Dominio</TableHead><TableHead>Estado</TableHead><TableHead>Actualizado</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>{sortedLogos.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Todavía no se ha buscado ningún logo — aparecen al recibir correo de empresas</TableCell></TableRow> : sortedLogos.map((l) => (
                  <TableRow key={l.domain} data-testid="logo-row">
                    <TableCell><LogoPreview domain={l.domain} ok={l.ok} version={logoVersions[l.domain] || 0} /></TableCell>
                    <TableCell className="font-medium break-all">{l.domain}</TableCell>
                    <TableCell>
                      {l.manual
                        ? <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: 'var(--accent-soft)', color: 'var(--accent-2)' }}>Manual</span>
                        : l.ok
                          ? <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: 'var(--good-soft)', color: 'var(--good)' }}>Automático</span>
                          : <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: 'var(--warn-soft)', color: 'var(--warn)' }}>Sin logo</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{l.fetched_at ? fmtFull(l.fetched_at) : '—'}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => openLogoDialog(l.domain)} data-testid="logo-upload"><Upload className="h-3.5 w-3.5" />{l.ok ? 'Reemplazar' : 'Subir'}</Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 ml-1 text-destructive" title="Quitar logo" aria-label="Quitar logo" onClick={() => deleteLogo(l)} data-testid="logo-delete"><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>))}</TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card data-testid="admin-users-table">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Correo</TableHead>
                  <TableHead>Buzones</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>2FA</TableHead>
                  <TableHead>Último acceso</TableHead>
                  <TableHead className="text-right">Buzón</TableHead>
                </TableRow></TableHeader>
                <TableBody>{users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {(u.accounts || []).length === 0
                        ? <span className="text-muted-foreground">-</span>
                        : (u.accounts || []).map((a) => a.email).join(', ')}
                      {u.indexed > 0 && <span className="ml-2 text-xs opacity-60">({u.indexed} en cache)</span>}
                      {/* El proveedor rechaza la contraseña guardada: ni envía ni recibe hasta que la persona vuelva a entrar (A2). */}
                      {(u.accounts || []).filter((a) => a.acceso_rechazado).map((a) => (
                        <div key={a.id} className="mt-1" data-testid={`admin-acceso-rechazado-${a.id}`}>
                          <Badge variant="destructive" className="font-normal">
                            Contraseña rechazada{a.acceso_rechazado_at ? ` desde ${fmtFull(a.acceso_rechazado_at)}` : ''}
                          </Badge>
                        </div>
                      ))}
                    </TableCell>
                    <TableCell><Badge variant="secondary">{u.role}</Badge></TableCell>
                    <TableCell>{u.totp_enabled ? <Badge>Activo</Badge> : <span className="text-muted-foreground text-sm">No</span>}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.last_seen ? fmtFull(u.last_seen) : '-'}</TableCell>
                    <TableCell className="text-right">
                      {u.id === me?.id ? (
                        <span className="text-xs text-muted-foreground">tu cuenta</span>
                      ) : (
                        <span className="inline-flex gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 rounded-[10px]"
                          onClick={() => setConfirmar({
                            titulo: `¿Cerrar todas las sesiones de ${u.email}?`,
                            mensaje: 'Saldrá en el acto de la web, la app y el programa de escritorio. Podrá volver a entrar con su contraseña; si no debe, bloquéalo o desactiva su buzón en el proveedor.',
                            boton: 'Cerrar sesiones',
                            accion: () => cerrarSesiones(u),
                          })}
                          data-testid={`admin-cerrar-sesiones-${u.id}`}
                        >
                          <LogOut className="h-4 w-4 mr-1.5" />Cerrar sesiones
                        </Button>
                        {u.totp_enabled && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 rounded-[10px]"
                            onClick={() => setConfirmar({
                              titulo: `¿Quitar la verificación en dos pasos de ${u.email}?`,
                              mensaje: 'Hazlo solo si la persona perdió el teléfono o su app de códigos y te lo pidió en persona o por teléfono. Podrá entrar solo con su contraseña hasta que la vuelva a activar.',
                              boton: 'Quitar 2FA',
                              accion: async () => {
                                try {
                                  await api.post(`/admin/users/${u.id}/2fa/reset`);
                                  toast.success(`Se quitó el 2FA de ${u.email}`);
                                  qc.invalidateQueries({ queryKey: ['admin-users'] });
                                } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo quitar el 2FA'); }
                              },
                            })}
                            data-testid={`admin-reset-2fa-${u.id}`}
                          >
                            Restablecer 2FA
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 rounded-[10px]"
                          onClick={() => entrarBuzon(u)}
                          disabled={!!entrando}
                          data-testid={`admin-impersonate-${u.id}`}
                        >
                          <LogIn className="h-4 w-4 mr-1.5" />
                          {entrando === u.id ? 'Entrando…' : 'Entrar'}
                        </Button>
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
              <div className="px-5 py-4 border-t text-xs text-muted-foreground">
                Entrar a un buzón abre el correo de esa persona con tu sesión de administrador
                detras: puedes leer, marcar, mover, responder y enviar igual que ella. Queda
                registrado en Auditoría a tu nombre y el acceso caduca solo; para volver al
                tuyo, "Salir del buzón" en la franja de arriba.
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="grupos">
            <PanelGrupos usuarios={users} />
          </TabsContent>

          <TabsContent value="marca">
            <PanelMarcaApp />
          </TabsContent>
          <TabsContent value="sso">
            <PanelSso yo={me} />
          </TabsContent>

          <TabsContent value="sync" className="space-y-4">
            <Card className="p-5 flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="font-medium flex items-center gap-2"><FolderSync className="h-4 w-4 text-primary" />Sincronización de buzones</div>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                  Los encabezados se copian progresivamente a la caché para que la bandeja abra al instante,
                  sin saturar al proveedor. El contenido de cada correo se lee del servidor al abrirlo.
                </p>
              </div>
              <Button variant="outline" className="gap-1.5" onClick={() => refetchSync()} disabled={syncFetching} data-testid="sync-refresh"><RefreshCw className={`h-4 w-4 ${syncFetching ? 'animate-spin' : ''}`} />Actualizar</Button>
            </Card>
            <Card data-testid="admin-sync-table">
              <Table>
                <TableHeader><TableRow><TableHead>Cuenta</TableHead><TableHead>Carpeta</TableHead><TableHead>Indexados</TableHead><TableHead>Estado</TableHead><TableHead>Última sincronización</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>{sync.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Aún no hay buzones sincronizados — aparecen tras el primer inicio de sesión</TableCell></TableRow> : sync.map((s) => (
                  <TableRow key={`${s.account_id}:${s.folder}`} data-testid="sync-row">
                    <TableCell className="font-medium">{s.email || s.account_id}</TableCell>
                    <TableCell>{FOLDER_LABELS[s.folder] || s.folder}</TableCell>
                    <TableCell className="text-sm">{s.indexed}{s.total_estimate ? ` / ${s.total_estimate}` : ''}</TableCell>
                    <TableCell>
                      {s.last_error ? <Badge variant="destructive" title={s.last_error}>Error</Badge>
                        : s.backfill_done ? <Badge>Completa</Badge> : <Badge variant="outline">En progreso</Badge>}
                      {s.last_error && <div className="text-xs text-destructive mt-1 truncate max-w-[200px]" title={s.last_error}>{s.last_error}</div>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.last_sync_at ? fmtFull(s.last_sync_at) : '—'}</TableCell>
                    <TableCell className="text-right"><Button variant="outline" size="sm" className="gap-1.5" onClick={() => reindex(s)} data-testid="sync-reindex"><RefreshCw className="h-3.5 w-3.5" />Reindexar</Button></TableCell>
                  </TableRow>))}</TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="security">
            <Card data-testid="admin-blocks-table">
              <div className="p-4 border-b border-border text-sm text-muted-foreground">Bloqueos de seguridad: cuentas o IPs bloqueadas por intentos fallidos / fuerza bruta. Elimina un bloqueo para desbloquear.</div>
              <Table>
                <TableHeader><TableRow><TableHead>Tipo</TableHead><TableHead>Valor</TableHead><TableHead>Motivo</TableHead><TableHead>Duración</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>{blocks.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No hay bloqueos activos</TableCell></TableRow> : blocks.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell><Badge variant={b.type === 'ip' ? 'destructive' : 'secondary'}>{b.type === 'ip' ? 'IP' : 'Cuenta'}</Badge></TableCell>
                    <TableCell className="font-mono text-sm">{b.value}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{b.reason}</TableCell>
                    <TableCell className="text-sm">{b.permanent ? 'Hasta desbloqueo' : `Temporal (${fmtFull(b.until)})`}</TableCell>
                    <TableCell className="text-right"><Button variant="outline" size="sm" className="gap-1.5" onClick={() => unblock(b)} data-testid="unblock-btn"><Unlock className="h-3.5 w-3.5" />Desbloquear</Button></TableCell>
                  </TableRow>))}</TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="audit">
            <Card data-testid="admin-audit-table">
              <Table>
                <TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Usuario</TableHead><TableHead>Acción</TableHead><TableHead>Detalle</TableHead></TableRow></TableHeader>
                <TableBody>{audit.map((a) => (<TableRow key={a.id}><TableCell className="text-sm">{fmtFull(a.created_at)}</TableCell><TableCell className="text-sm">{a.user_email}</TableCell><TableCell><Badge variant="outline">{a.action}</Badge></TableCell><TableCell className="text-sm text-muted-foreground truncate max-w-[280px]">{a.detail}</TableCell></TableRow>))}</TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="sessions">
            <Card>
              <Table>
                <TableHeader><TableRow><TableHead>Dispositivo</TableHead><TableHead>IP</TableHead><TableHead>Última actividad</TableHead></TableRow></TableHeader>
                <TableBody>{sessions.map((s) => (<TableRow key={s.id}><TableCell className="text-sm truncate max-w-[320px]">{s.device}</TableCell><TableCell className="text-sm">{s.ip}</TableCell><TableCell className="text-sm">{fmtFull(s.last_seen)}</TableCell></TableRow>))}</TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialogo alta/edición de dominio */}
        <Dialog open={!!domDialog} onOpenChange={(o) => !o && setDomDialog(null)}>
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader><DialogTitle>{domDialog?.mode === 'create' ? 'Agregar dominio' : `Editar ${domDialog?.form?.domain}`}</DialogTitle></DialogHeader>
            {domDialog && (
              <div className="space-y-3">
                <div className="space-y-1.5"><Label htmlFor="domain-input">Dominio</Label><Input id="domain-input" value={domDialog.form.domain} onChange={(e) => setForm({ domain: e.target.value })} placeholder="empresa.com" data-testid="domain-input" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5 col-span-2"><Label htmlFor="domain-imap-host">Servidor IMAP</Label><Input id="domain-imap-host" value={domDialog.form.imap_host} onChange={(e) => setForm({ imap_host: e.target.value })} data-testid="domain-imap-host" /></div>
                  <div className="space-y-1.5"><Label htmlFor="domain-imap-port">Puerto</Label><Input id="domain-imap-port" type="number" value={domDialog.form.imap_port} onChange={(e) => setForm({ imap_port: e.target.value })} data-testid="domain-imap-port" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5 col-span-2"><Label htmlFor="domain-smtp-host">Servidor SMTP</Label><Input id="domain-smtp-host" value={domDialog.form.smtp_host} onChange={(e) => setForm({ smtp_host: e.target.value })} data-testid="domain-smtp-host" /></div>
                  <div className="space-y-1.5"><Label htmlFor="domain-smtp-port">Puerto</Label><Input id="domain-smtp-port" type="number" value={domDialog.form.smtp_port} onChange={(e) => setForm({ smtp_port: e.target.value })} data-testid="domain-smtp-port" /></div>
                </div>
                <div className="space-y-1.5"><Label htmlFor="domain-notes">Notas</Label><Input id="domain-notes" value={domDialog.form.notes} onChange={(e) => setForm({ notes: e.target.value })} placeholder="Opcional" data-testid="domain-notes" /></div>
                <div className="flex items-center gap-2"><Switch checked={domDialog.form.enabled} onCheckedChange={(v) => setForm({ enabled: v })} /><span className="text-sm">Dominio activo (permite iniciar sesión)</span></div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDomDialog(null)}>Cancelar</Button>
              <Button onClick={saveDomain} data-testid="domain-save">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialogo subir logo de remitente */}
        <Dialog open={!!logoDialog} onOpenChange={(o) => !o && closeLogoDialog()}>
          <DialogContent className="sm:max-w-[440px]">
            <DialogHeader><DialogTitle>{logoDialog?.domain ? `Logo de ${normalizeDomain(logoDialog.domain) || logoDialog.domain}` : 'Añadir logo'}</DialogTitle></DialogHeader>
            {logoDialog && (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="logo-dominio">Dominio del remitente</Label>
                  <Input
                    id="logo-dominio"
                    value={logoDialog.domain}
                    onChange={(e) => setLogoDialog((s) => ({ ...s, domain: e.target.value }))}
                    placeholder="empresa.com"
                    data-testid="logo-domain-input"
                  />
                  <p className="text-xs text-muted-foreground">Puedes pegar un correo completo: se queda con el dominio.</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="logo-imagen">Imagen</Label>
                  <div className="flex items-center gap-3">
                    <div
                      className="grid place-items-center overflow-hidden shrink-0"
                      style={{ width: 56, height: 56, borderRadius: 12, background: logoDialog.previewUrl ? 'var(--panel-2)' : 'var(--hover)', border: '1px solid var(--line)' }}
                    >
                      {logoDialog.previewUrl
                        ? <img src={logoDialog.previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} />
                        : <ImageIcon className="h-5 w-5" style={{ color: 'var(--text-3)' }} />}
                    </div>
                    <Input
                      id="logo-imagen"
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => pickLogoFile(e.target.files?.[0])}
                      data-testid="logo-file-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">PNG, JPG o WebP · máximo 2 MB · se recorta a 128 px.</p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={closeLogoDialog}>Cancelar</Button>
              <Button onClick={saveLogo} disabled={logoDialog?.busy} className="gap-1.5" data-testid="logo-save"><Upload className="h-4 w-4" />{logoDialog?.busy ? 'Guardando…' : 'Guardar'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialogo probar conexión */}
        <Dialog open={!!testDialog} onOpenChange={(o) => !o && setTestDialog(null)}>
          <DialogContent className="sm:max-w-[440px]">
            <DialogHeader><DialogTitle>Probar conexión — {testDialog?.domain}</DialogTitle></DialogHeader>
            {testDialog && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Se prueba un inicio de sesión IMAP real con un buzón de este dominio. No se guarda nada.</p>
                <div className="space-y-1.5"><Label htmlFor="domain-test-email">Correo de prueba</Label><Input id="domain-test-email" value={testDialog.email} onChange={(e) => setTestDialog({ ...testDialog, email: e.target.value })} placeholder={`persona@${testDialog.domain}`} data-testid="domain-test-email" /></div>
                <div className="space-y-1.5"><Label htmlFor="domain-test-password">Contraseña</Label><Input id="domain-test-password" type="password" value={testDialog.password} onChange={(e) => setTestDialog({ ...testDialog, password: e.target.value })} data-testid="domain-test-password" /></div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setTestDialog(null)}>Cerrar</Button>
              <Button onClick={runTest} disabled={testDialog?.busy} className="gap-1.5" data-testid="domain-test-run"><PlugZap className="h-4 w-4" />{testDialog?.busy ? 'Probando…' : 'Probar'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <DialogoConfirmar
          open={!!confirmar}
          titulo={confirmar?.titulo || ''}
          mensaje={confirmar?.mensaje || ''}
          boton={confirmar?.boton}
          testId="admin-confirmar"
          tono={confirmar?.tono}
          onOpenChange={(v) => !v && setConfirmar(null)}
          onConfirmar={() => { const a = confirmar?.accion; setConfirmar(null); a?.(); }}
        />
      </div>
    </div>
  );
}
